import {
  Box,
  Checkbox,
  CheckboxGroup,
  LinkButton,
  Menu,
  MenuToggle,
  MenuItem,
  MenuList,
  Scrollbar,
  MenuProps,
} from '@tonic-one/react';
import { noop } from '@tonic-one/utils';
import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type MultiSelectProps = {
  value: string[];
  /**
   * @param value all checked values
   * @description handle all checked values
   */
  onChange: (value: string[]) => void;
  options: string[];
  renderOption: (v: string) => React.ReactNode;
  shouldSelectAllIfNoneSelected?: boolean;
  children: React.ReactNode | ((props: any) => React.ReactNode);
  maxHeight?: number;
} & Omit<MenuProps, 'onChange' | 'children'>;

const MultiSelect = forwardRef<any, MultiSelectProps>(
  (
    {
      children,
      options = [],
      renderOption = noop,
      shouldSelectAllIfNoneSelected = false,
      onChange: onChangeProp,
      value: valueProp,
      maxHeight,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation();
    const [value, setValue] = useState(valueProp);

    useEffect(() => {
      const isControlled = valueProp !== undefined;
      if (isControlled) {
        setValue(valueProp);
      }
    }, [valueProp]);

    const onCheckboxGroupChange = useCallback((nextValue: string[]) => {
      setValue(nextValue);
    }, []);

    const isAllSelected = value.length === options.length;
    const isNoneSelected = value.length === 0;
    const handleClickToggle = () => {
      const nextValue = isAllSelected ? [] : options;
      setValue(nextValue);
    };

    const renderOptions = () => {
      return (
        <>
          {options.map((option) => {
            const key = option;
            return (
              <MenuItem key={key}>
                <Checkbox value={option} width="100%">
                  {renderOption(option)}
                </Checkbox>
              </MenuItem>
            );
          })}
        </>
      );
    };

    return (
      <Menu
        ref={ref}
        closeOnSelect={false}
        defaultActiveIndex={0}
        onClose={() => {
          if (isNoneSelected && shouldSelectAllIfNoneSelected) {
            // Automatically reset all the options when the menu loses focus
            setValue(options);
            onChangeProp(options);
          } else {
            onChangeProp(value);
          }
        }}
        {...rest}
      >
        <MenuToggle>{children}</MenuToggle>
        <MenuList width="max-content">
          <CheckboxGroup
            name=""
            size="sm"
            value={value}
            onChange={onCheckboxGroupChange}
          >
            <Box px="3x" mb="2x">
              <LinkButton key="toggle" onClick={handleClickToggle}>
                {isAllSelected
                  ? t('common.service.clearAll')
                  : t('common.service.selectAll')}
              </LinkButton>
            </Box>

            {maxHeight ? (
              <Scrollbar maxHeight={maxHeight} overflowY="auto">
                {renderOptions()}
              </Scrollbar>
            ) : (
              <>{renderOptions()}</>
            )}
          </CheckboxGroup>
        </MenuList>
      </Menu>
    );
  }
);

export default MultiSelect;
